<template>
	<div class="c-cart__inner">
		<template v-if="!isLoading">
			<div v-if="basketCheckResult.numberOfProducts > 0" class="cxs c-cart__missingitems">
				<div class="c-cart__missingitems--heading"><i class="far fa-exclamation-triangle"></i><span>Let op! Er zijn artikelen verwijderd</span></div>
				<div class="c-cart__missingitems--content">{{ basketCheckResult.message }}</div>
			</div>
			<div v-if="products.length === 0" class="c-cart__noresults">
				<p>Uw winkelwagen bevat (nog) geen artikelen.</p>
				<div class="cxs c-buttonlist justify-content-between">
					<a class="btn btn-default btn-arrow-left" href="/assortiment">
						Verder winkelen
					</a>
				</div>
			</div>
			<template v-else>
				<div v-if="warningMessage.active" class="cxs c-cart__missingitems">
					<div class="c-cart__missingitems--heading">
						<i class="far fa-exclamation-triangle"></i>
						<span>{{ warningMessage.text }}</span>
					</div>
				</div>
				<div class="cxs c-cart__items">
					<div class="c-cart__head">
						<div class="c-cart__head--info">
							Artikel
						</div>
						<div class="c-cart__head--amount">
							Aantal
						</div>
						<div class="c-cart__head--remove" />
						<div class="c-cart__head--price">
							Nettoprijs
						</div>
						<div class="c-cart__head--totalprice">
							Subtotaal
						</div>
					</div>
					<div v-for="product in products" :key="product.Id" class="c-cart__item">
						<div class="c-cart__item--info">
							<div class="c-cart__item--info--title">
								{{ product.ItemDescription }}
							</div>
							<div class="c-cart__item--info--artnr">Artikelnummer: {{ product.ItemNo }}</div>
							<div class="c-cart__item--info--price isnumeric d-block d-md-none">Nettoprijs: {{ product.CustomerPrice | toCurrency }}</div>
							<div v-if="!isLoadingStock" class="c-cart__item--info--stock" v-html="getProductAmountInStock(product)" />
							<div v-else class="c-cart__item--info--stock">
								<span>&nbsp;</span>
							</div>
						</div>
						<div class="c-cart__item--amount">
							<div class="c-product__amount--control">

								<div
									:class="['c-product__amount--minus', { disabled: product.Amount <= product.PriceOrderRatio }]"
									tabindex="0"
									@click="changeItemAmount(product.Id, product.Amount - product.PriceOrderRatio)"
									@keyup.enter="changeItemAmount(product.Id, product.Amount - product.PriceOrderRatio)"
								>
									<i class="far fa-minus" />
									<span class="sr-only">Minder</span>
								</div>
								<div class="c-product__amount--nr">
									<input v-if="product.PriceOrderRatio % 1 !== 0" :disabled="product.PriceOrderRatio != 1" :value="getDisplayAmount(product.Amount)" />
									<input v-else type="number" :disabled="product.PriceOrderRatio != 1" :value="product.Amount" @change="updateItemAmount($event, product.Id)" />
								</div>
								<div
									class="c-product__amount--plus"
									tabindex="0"
									@click="changeItemAmount(product.Id, product.Amount + product.PriceOrderRatio)"
									@keyup.enter="changeItemAmount(product.Id, product.Amount + product.PriceOrderRatio)"
								>
									<i class="far fa-plus" />
									<span class="sr-only">Meer</span>
								</div>
							</div>
						</div>
						<div class="c-cart__item--remove">
							<a class="btn btn-link btn-icononly" @click="removeItemFromBasket(product.Id)"><i class="far fa-trash fa-lg" /><span class="sr-only">Verwijder</span></a>
						</div>
						<div class="c-cart__item--price">
							<span class="isnumeric">x {{ product.CustomerPrice | toCurrency }}</span>
						</div>
						<div class="c-cart__item--totalprice">
							<span class="isnumeric font-weight-medium">{{ product.TotalCustomerPrice | toCurrency }}</span>
						</div>
					</div>
				</div>
				<div class="cxs c-cart__summary bg-light">
					<div class="c-cart__summary--inner">
						<div class="c-cart__summary--item">
							<span>Producten</span>
							<span class="isnumeric">{{ totalCustomerPriceAllProducts | toCurrency }}</span>
						</div>
						<div class="c-cart__summary--item">
							<span>Afleverkosten</span>
							<span class="font-weight-medium text-success">Geen</span>
						</div>
						<div class="c-cart__summary--item">
							<span class="font-weight-medium">Totaal</span>
							<span class="isnumeric font-weight-medium">{{ totalCustomerPriceAllProducts | toCurrency }}</span>
						</div>
					</div>
				</div>
				<div class="cxs c-buttonlist justify-content-between">
					<a class="btn btn-default btn-arrow-left" href="/assortiment">
						Verder winkelen
					</a>
					<a href="/bestellen" class="btn btn-success btn-arrow">
						Bestelling afronden
					</a>
				</div>
			</template>
		</template>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	props: {
		basketSettings: {
			type: Object,
			required: true
		}
	},
	data: () => ({
		basketClient: null,
		basketId: null,
		basketCheckResult: {
			message: null,
			numberOfProducts: 0
		},
		products: [],
		amountInBasket: localStorage.getItem('amountInBasket'),
		isLoading: true,
		isLoadingStock: true,
		warningMessage: {
			active: false,
			text: ''
		}
	}),
	computed: {
		totalCustomerPriceAllProducts() {
			let totalPrice = 0;

			if (this.products.length > 0) {
				for (var i = 0, _len = this.products.length; i < _len; i++) {
					totalPrice += this.products[i].TotalCustomerPrice;
				}
			}

			return this.toRoundedPrice(totalPrice);
		}
	},
	created() {
		this.basketClient = axios.create({
			baseURL: '/api'
		});
		this.basketClient.defaults.headers.common['Content-Type'] = 'application/json';
		this.basketClient.defaults.timeout = 0;
		this.basketClient.defaults.method = 'post';

		// get customer basket from ABP (via the BasketController)
		this.basketClient
			.post('/winkelwagen/index')
			.then(response => {
				if (response.data != null && response.data !== '' && typeof response.data.BasketItems === 'object' && response.data.BasketItems.length > 0) {
					this.products = response.data.BasketItems;
					this.basketId = response.data.Id;
					this.products.forEach(p => {
						const data = JSON.parse(p.Data);
						p.PriceOrderRatio = data.priceOrderRatio;
						p.Unit = data.unit.toLowerCase();
					}); 
				}

				this.isLoading = false;
				this.updateLocalStorageAndDispatchEvent();

				if (this.products != null && this.products.length > 0) {
					this.refreshNetPrices();
					this.refreshWarehouseQuantities();
				}
			})
			.catch(e => {
				// console.error(e);
			})
			.finally(() => {
				this.isLoading = false;

				// check for existence of the products (they can be out of assortiment)
				this.basketClient
					.post('/winkelwagen/check')
					.then(response => {
						// set reponse text
						this.basketCheckResult.message = response.data.Message;
						this.basketCheckResult.numberOfProducts = response.data.Articles.length;
						
						for (var i = 0, _len = response.data.Articles.length; i < _len; i++) {
							this.removeItemFromLocalBasket(response.data.Articles[i].Id);
						}
					})
					.catch(e => {
						// console.error(e);
					});
			});		
	},
	methods: {
		getDisplayAmount(amount){
			const amountString = amount + '';
			return amountString.replace('.',',');
		},
		getProductAmountInStock(product) {
			let amountStock = 0;
			if (product == null || !('ExtraProperties' in product) || !('AmountInStock' in product.ExtraProperties)) {
				amountStock = 0;
			} else {
				amountStock = product.ExtraProperties.AmountInStock;
			}
			if (amountStock > 20) {
				return '<span class="instock text-success"><i class="far fa-check"></i><span>' + amountStock + ' ' + product.Unit +  ' op voorraad</span></span>';
			} else if (amountStock > 0) {
				return '<span class="lowstock text-warning"><i class="far fa-exclamation-triangle"></i><span>' + amountStock + ' ' + product.Unit +  ' op voorraad</span></span>';
			} else {
				return '<span class="nostock text-danger"><i class="far fa-exclamation-triangle"></i><span>Niet op voorraad</span></span>';
			}
		},
		removeItemFromBasket(guid) {
			if (this.products === null || this.products.length === 0 || guid === null || guid === '') {
				return;
			}

			// get item from basket (item-list)
			const productToRemove = this.products.filter(i => i.Id === guid);
			if (productToRemove.length === 0) {
				return;
			}

			// remove item
			this.basketClient
				.post('/winkelwagen/item/remove', productToRemove[0])
				.then(response => {
					this.removeItemFromLocalBasket(guid);
					this.updateLocalStorageAndDispatchEvent();
				})
				.catch(e => {
					// console.error(e);
				});
		},
		removeItemFromLocalBasket(guid) {
			var productKeyToRemove = this.products.findIndex(i => i.Id === guid);
			this.products.splice(productKeyToRemove, 1);
		},
		refreshNetPrices() {
			if (this.products === null || this.products.length === 0) return;

			const productCodes = this.products.map(p => p.ItemNo);

			this.basketClient
				.post('/productoverview/netprices', productCodes)
				.then(netPrices => {
					if (this.products.length > 0) {
						for (var i = 0, _len = this.products.length; i < _len; i++) {
							const _netPrice = netPrices.data.filter(x => x.AtricleCode === this.products[i].ItemNo);
							if (_netPrice.length === 1) {
								this.products[i].CustomerPrice = _netPrice[0].NettoPrijs;
								this.products[i].TotalCustomerPrice = this.products[i].Amount * _netPrice[0].NettoPrijs;
							}
						}
					}
				})
				.catch(e => {
					// console.error(e);
				});
		},
		refreshWarehouseQuantities() {
			if (this.products === null || this.products.length === 0) return;

			const productCodes = this.products.map(p => p.ItemNo);

			this.basketClient
				.post('/productoverview/warehousequantities', productCodes)
				.then(warehouseQuantities => {
					if (this.products.length > 0) {
						for (var i = 0, _len = this.products.length; i < _len; i++) {
							const _productQuantities = warehouseQuantities.data.filter(x => x.AtricleCode === this.products[i].ItemNo);
							if (_productQuantities.length > 0) {
								// find the rounding level
								let mostAccurateDecimalLength = 0;
								_productQuantities.forEach(quantity =>{
									if(!Number.isInteger(quantity.QuantityFree)){
										const quantityString = quantity.QuantityFree + '';
										if(quantityString.split('.')[1].length > mostAccurateDecimalLength){
											mostAccurateDecimalLength = quantityString.toString().split('.')[1].length;
										}
									}
								});								
								const totalQuantity = _productQuantities.map(wh => wh.QuantityFree).reduce((prev, next) => prev + next);
								this.products[i].ExtraProperties.AmountInStock = totalQuantity.toFixed(mostAccurateDecimalLength);
							}
						}
					}
					this.isLoadingStock = false;
				})
				.catch(e => {
					console.error(e);
				});
		},
		updateItemAmount(event, guid) {
			this.changeItemAmount(guid,event.target.valueAsNumber);
		},
		changeItemAmount(guid, newAmount) {
			const productKey = this.products.findIndex(i => i.Id === guid);
			if (productKey == null) {
				return;
			}

			// change amount and total prices
			if(this.products[productKey].PriceOrderRatio % 1 !== 0){
				// is float
				// get the level of accuracy of the PriceOrderRatio to do proper rounding for the amount
				// to prevent for example 0.44 + 0.44 = 0.880000000000001 
				const priceOrderRatioString = this.products[productKey].PriceOrderRatio + '';
				const priceOrderRatioAccuracy = priceOrderRatioString.split('.')[1].length;

				this.products[productKey].Amount = parseFloat(newAmount.toFixed(priceOrderRatioAccuracy));
			}else{
				// is int
				if (newAmount < 1) {
					newAmount = 1;
				}
				this.products[productKey].Amount = parseInt(newAmount);

			}
			this.products[productKey].TotalStandardPrice = this.toRoundedPrice(this.products[productKey].Amount * parseFloat(this.products[productKey].StandardPrice));
			this.products[productKey].TotalCustomerPrice = this.toRoundedPrice(this.products[productKey].Amount * parseFloat(this.products[productKey].CustomerPrice));

			this.basketClient
				.post('/winkelwagen/item/update', this.products[productKey])
				.then(response => {
					this.updateLocalStorageAndDispatchEvent();
				})
				.catch(e => {
					// console.error(e);
				});
		},
		updateLocalStorageAndDispatchEvent() {
			
			if (this.products == null || typeof this.products === 'undefined') {
				return;
			}
		
			let mostPrecisePriceOrderRatio = 0;
			this.amountInBasket = 0;
			for (var i = 0, _len = this.products.length; i < _len; i++) {
				this.amountInBasket += this.products[i].Amount;			
				if(!Number.isInteger(this.products[i].Amount)){
					const priceOrderRatioString = this.products[i].PriceOrderRatio + '';
					if(mostPrecisePriceOrderRatio < priceOrderRatioString.split('.')[1].length){
						mostPrecisePriceOrderRatio = priceOrderRatioString.split('.')[1].length;
					}						
				}
			}
			
			this.amountInBasket = parseFloat(this.amountInBasket.toFixed(mostPrecisePriceOrderRatio));

			localStorage.setItem('amountInBasket', this.amountInBasket);
			localStorage.setItem('basketId', this.basketId);

			window.dispatchEvent(
				new CustomEvent('haenka-basket-changed', {
					detail: {
						amount: this.amountInBasket
					}
				})
			);
		},
		toRoundedPrice(price) {
			return Math.round((price + Number.EPSILON) * 100) / 100;
		}
	}
};
</script>
